import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCitySearch } from "@/hooks/useCitySearch";
import { useGroupedSupplierCategories } from "@/hooks/useSupplierCategories";
import { supabase } from "@/integrations/supabase/client";
import { MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { generateCitySlug } from "@/utils/locationMappings";
import { LOCATION_PAGE_ROUTES } from "@/config/categoryRoutes";

// Defines the props for the component
interface WeddingSearchProps {
  showTitle?: boolean;
  buttonText?: string;
  className?: string;
}

// Function to get the URL path for a category
export const getCategoryPath = async (id: string): Promise<string> => {
  const { data, error } = await supabase
    .from("supplier_categories")
    .select("url_path")
    .eq("id", id)
    .single();

  if (error || !data) {
    return "/";
  }

  return `/${data.url_path}`;
};


// Main component
export const WeddingSearch = ({
  showTitle = true,
  buttonText = "Hitta leverantör 🔍",
  className = ""
}: WeddingSearchProps) => {
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("7f8a6ada-dac7-4aee-85db-2ef0cb00ae19");
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [link, setLink] = useState(`/${LOCATION_PAGE_ROUTES.VENUES_LOCATIONS}`);
  const [isSearching, setIsSearching] = useState(false);

  const navigate = useNavigate();
  const { results, isLoading: isCityLoading } = useCitySearch(locationSearch);
  const { data: groupedCategories, isLoading: categoriesLoading, error: categoriesError } = useGroupedSupplierCategories();

  // Handlers for input and popover state
  const handleLocationSelect = (locationName: string) => {
    setLocationSearch(locationName);
    setIsLocationOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationSearch(value);
    setIsLocationOpen(value.length > 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsLocationOpen(false);
    }
  };

  // Effect to update the link whenever the selected category changes
  useEffect(() => {
    if (selectedCategory) {
      getCategoryPath(selectedCategory).then(setLink);
    }
  }, [selectedCategory]);

  // Handle search button click
  const handleSearch = async () => {
    if (!selectedCategory || !link) {
      toast.error("Välj en kategori först");
      return;
    }

    setIsSearching(true);

    try {
      if (!locationSearch || !locationSearch.trim()) {
        // No location - navigate to category page
        navigate(link);
      } else {
        // Always generate city slug from user input
        const citySlug = generateCitySlug(locationSearch);
        navigate(`${link}/${citySlug}`);
      }
    } catch (error) {
      console.error("Error during search:", error);
      toast.error("Ett fel uppstod vid sökningen");
    } finally {
      setIsSearching(false);
    }
  };

  // Helper function to render categories
  const renderCategories = () => {
    if (categoriesLoading) {
      return <SelectItem value="loading" disabled>Laddar kategorier...</SelectItem>;
    }
    if (categoriesError) {
      return <SelectItem value="error" disabled>Kunde inte ladda kategorier</SelectItem>;
    }
    if (!groupedCategories || Object.keys(groupedCategories).length === 0) {
      return <SelectItem value="empty" disabled>Inga kategorier tillgängliga</SelectItem>;
    }

    return Object.entries(groupedCategories).map(([categoryNumber, group], index) => {
      if (group.subCategories.length === 0) return null;
      return (
        <div key={categoryNumber}>
          {index > 0 && <SelectSeparator />}
          <SelectGroup>
            <SelectLabel>{group.mainCategory.name}</SelectLabel>
            {group.subCategories.map((subCategory) => (
              <SelectItem key={subCategory.id} value={subCategory.id}>
                {subCategory.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </div>
      );
    }).filter(Boolean);
  };

  return (
    <div className={className}>
      {showTitle && (
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Hitta den perfekta leverantören
        </h2>
      )}

      {/* Popover wrapper for location search */}
      <Popover open={isLocationOpen} onOpenChange={setIsLocationOpen}>
        <div className="flex flex-col lg:flex-row gap-4 max-w-[800px] mx-auto mb-8 sm:mb-12 lg:mb-16 px-4 sm:px-0">

          {/* Category Select */}
          <div className="w-full lg:flex-1">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger
                className="h-[54px] sm:h-[56px] bg-white rounded-full border-none text-gray-600 text-base shadow-lg min-h-[44px] px-6"
                aria-label="Välj leverantörskategori"
              >
                <SelectValue placeholder={categoriesLoading ? "Laddar..." : "Välj kategori"} />
              </SelectTrigger>
              <SelectContent>{renderCategories()}</SelectContent>
            </Select>
          </div>

          {/* Location Input */}
          <div className="w-full lg:flex-1 relative">
            <PopoverTrigger asChild>
              <div className="relative">
                <MapPin className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" aria-hidden="true" />
                <Input
                  id="location-search-input"
                  value={locationSearch}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => locationSearch.length > 0 && setIsLocationOpen(true)}
                  placeholder="i hela Sverige"
                  aria-label="Sök efter stad eller plats"
                  aria-expanded={isLocationOpen}
                  aria-haspopup="listbox"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="location-results-listbox"
                  className="h-[54px] sm:h-[56px] bg-white rounded-full border-none pl-14 pr-6 text-gray-600 text-base shadow-lg min-h-[44px]"
                />
              </div>
            </PopoverTrigger>
          </div>

          {/* Search Button */}
          <div className="w-full lg:flex-1">
            <Button
              onClick={handleSearch}
              disabled={isSearching || categoriesLoading}
              className="bg-[#aabcbf] hover:bg-[#9aacaf] text-white border border-white px-8 h-[54px] sm:h-[56px] rounded-full w-full text-base font-bold shadow-lg hover:shadow-xl transition-all duration-200 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? (
                <span>Söker...</span>
              ) : (
                <>
                  <span className="hidden sm:inline">{buttonText}</span>
                  <span className="sm:hidden flex items-center justify-center gap-1">
                    <span>🔍</span>
                    <span>Sök</span>
                  </span>
                </>
              )}
            </Button>
          </div>

        </div>

        {/* Popover Content for Location Search */}
        <PopoverContent
          className="w-full sm:w-96 p-0 bg-white border shadow-lg rounded-lg z-50 mt-2"
          align="start"
          side="bottom"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div
            id="location-results-listbox"
            role="listbox"
            className="max-h-64 overflow-y-auto"
          >
            {isCityLoading ? (
              <div className="p-4 text-center text-gray-500">Söker...</div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-gray-500">Inga resultat hittades</div>
            ) : (
              <div>
                <div className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">Städer</div>
                {results.map((result) => (
                  <button
                    key={result.id}
                    role="option"
                    aria-selected={locationSearch === result.name}
                    onClick={() => handleLocationSelect(result.name)}
                    className="w-full px-4 py-4 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 min-h-[44px] flex items-center"
                  >
                    <span className="text-gray-800 text-base">{result.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};